<script setup lang="ts">
import { Download, Eye, EyeOff, FolderInput, MoreHorizontal, Pencil, Star, Trash2 } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ResponsiveActionSubmenu from '@/components/ui/responsive-action-submenu/ResponsiveActionSubmenu.vue';
import { isImageUploadedFile } from '~/lib/uploadedFiles';
import type { UploadedFile } from '~/types/models';

interface ProjectOption {
    value: string;
    label: string;
}

const props = withDefaults(defineProps<{
    file: UploadedFile;
    projectOptions?: ProjectOption[];
    busy?: boolean;
    allowAddToProject?: boolean;
    allowSetCover?: boolean;
    allowTogglePortalVisibility?: boolean;
    allowEdit?: boolean;
    allowDelete?: boolean;
}>(), {
    projectOptions: () => [],
    busy: false,
    allowAddToProject: false,
    allowSetCover: false,
    allowTogglePortalVisibility: false,
    allowEdit: false,
    allowDelete: false,
});

const emit = defineEmits<{
    (event: 'add-to-project', payload: { file: UploadedFile, projectId: string }): void;
    (event: 'set-cover', file: UploadedFile): void;
    (event: 'toggle-portal-visibility', file: UploadedFile): void;
    (event: 'edit', file: UploadedFile): void;
    (event: 'delete', file: UploadedFile): void;
}>();

const assignableProjects = computed(() => props.projectOptions.map((project) => ({
    id: project.value,
    label: project.label,
    disabled: props.file.entityType === 'project' && props.file.entityId === project.value,
})));

const hasManagementActions = computed(() => props.allowAddToProject
    || props.allowSetCover
    || props.allowTogglePortalVisibility
    || props.allowEdit
    || props.allowDelete);
</script>

<template>
    <div class="flex shrink-0 items-center gap-1" @click.stop>
        <Button type="button" variant="ghost" size="icon-sm" class="size-8" as-child>
            <a :href="file.fileURL" :download="file.fileName" target="_blank" rel="noopener noreferrer" :aria-label="`Download ${file.fileName}`">
                <Download class="size-4" />
            </a>
        </Button>

        <DropdownMenu v-if="hasManagementActions">
            <DropdownMenuTrigger as-child>
                <Button type="button" variant="ghost" size="icon-sm" class="size-8" :disabled="busy" :aria-label="`Actions for ${file.fileName}`">
                    <MoreHorizontal class="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-52">
                <ResponsiveActionSubmenu
                    v-if="allowAddToProject && file.entityType === 'client'"
                    mode="dropdown"
                    label="Add to project"
                    :icon="FolderInput"
                    :items="assignableProjects"
                    empty-label="No linked projects available."
                    @select="emit('add-to-project', { file, projectId: $event.id })"
                />

                <DropdownMenuItem
                    v-if="allowSetCover && file.entityType === 'project' && isImageUploadedFile(file) && !file.isProjectCover"
                    @select="emit('set-cover', file)"
                >
                    <Star class="size-4" />
                    Set as project cover
                </DropdownMenuItem>

                <DropdownMenuItem
                    v-if="allowTogglePortalVisibility && file.entityType === 'project' && isImageUploadedFile(file)"
                    @select="emit('toggle-portal-visibility', file)"
                >
                    <EyeOff v-if="file.galleryVisibleInPortal !== false" class="size-4" />
                    <Eye v-else class="size-4" />
                    {{ file.galleryVisibleInPortal !== false ? 'Hide from client portal' : 'Show in client portal' }}
                </DropdownMenuItem>

                <DropdownMenuItem v-if="allowEdit && isImageUploadedFile(file)" @select="emit('edit', file)">
                    <Pencil class="size-4" />
                    Edit image details
                </DropdownMenuItem>

                <template v-if="allowDelete">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" @select="emit('delete', file)">
                        <Trash2 class="size-4" />
                        Delete file
                    </DropdownMenuItem>
                </template>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
</template>
